import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FolderOutlined } from '@ant-design/icons';

import Colors from '../../../../../constants/Colors';
import RedistributeHourStepper from './RedistributeHourStepper';
import RedistributeHourBadge from './RedistributeHourBadge';
import { formatHours, isHoursPositive, parseHour } from './redistributionHelpers';

const USER_HOUR_ROWS = [
  { label: 'REG', hourType: 'hours', badgeVariant: 'REG' },
  { label: 'OT', hourType: 'otHours', badgeVariant: 'OT' },
  { label: 'DT', hourType: 'doubleOTHours', badgeVariant: 'DT' },
];

const EQUIPMENT_HOUR_ROWS = [
  { label: 'HRS', hourType: 'hours', badgeVariant: 'neutral' },
];

export default function RedistributeCostCodeRow({
  column,
  cell,
  isEquipment,
  onHourDelta,
}) {
  const reg = parseHour(cell?.hours) || 0;
  const ot = parseHour(cell?.otHours) || 0;
  const dt = parseHour(cell?.doubleOTHours) || 0;
  const hourByType = { hours: reg, otHours: ot, doubleOTHours: dt };

  const handlers = useMemo(() => ({
    hours: (delta) => onHourDelta(column.rowId, 'hours', delta),
    otHours: (delta) => onHourDelta(column.rowId, 'otHours', delta),
    doubleOTHours: (delta) => onHourDelta(column.rowId, 'doubleOTHours', delta),
  }), [column.rowId, onHourDelta]);

  const ccText = column?.costcode?.fullText || column?.project?.name || 'Unassigned';
  const total = reg + ot + dt;

  const pairs = [
    { label: 'REG', value: reg },
    { label: 'OT', value: ot },
    { label: 'DT', value: dt },
  ].filter(({ value }) => isHoursPositive(value));

  const hourRows = isEquipment ? EQUIPMENT_HOUR_ROWS : USER_HOUR_ROWS;

  return (
    <div className="ttg-redistribute-cost-code-item">
      <div className="ttg-redistribute-cost-code-header">
        <div className="ttg-redistribute-cost-code-header-leading">
          <div className="ttg-redistribute-cost-code-folder-icon">
            <FolderOutlined style={{ fontSize: 18, color: Colors.BLUE_600 }} />
          </div>
          <div className="ttg-redistribute-cost-code-label-block">
            <div className="ttg-redistribute-cost-code-name">{ccText}</div>
          </div>
          {pairs.length > 0 && (
            <div className="ttg-redistribute-cost-code-coded-inline">
              <span className="ttg-redistribute-cost-code-coded-label">Cost Coded:</span>
              {pairs.map(({ label, value }) => (
                <RedistributeHourBadge
                  key={label}
                  variant={label}
                  value={value}
                  valueStylePreset="costCodeInline"
                  containerClassName="ttg-redistribute-cost-code-coded-pair"
                />
              ))}
            </div>
          )}
        </div>
        <div className="ttg-redistribute-cost-code-total-chip">
          <span className="ttg-redistribute-cost-code-total-chip-text">
            {`${formatHours(total)} total`}
          </span>
        </div>
      </div>
      <div className="ttg-redistribute-cost-code-steppers-row">
        {hourRows.map(({ label, hourType, badgeVariant }) => (
          <div key={hourType} className="ttg-redistribute-cost-code-stepper-cell">
            <RedistributeHourStepper
              label={label}
              value={hourByType[hourType]}
              badgeVariant={badgeVariant}
              onDelta={handlers[hourType]}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

RedistributeCostCodeRow.propTypes = {
  column: PropTypes.shape({
    rowId: PropTypes.string,
    project: PropTypes.shape({ name: PropTypes.string }),
    costcode: PropTypes.shape({ fullText: PropTypes.string }),
  }).isRequired,
  cell: PropTypes.shape({
    hours: PropTypes.number,
    otHours: PropTypes.number,
    doubleOTHours: PropTypes.number,
  }),
  isEquipment: PropTypes.bool.isRequired,
  onHourDelta: PropTypes.func.isRequired,
};
